import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SubscriptionPlan,
  SubscriptionUpgradeRequest,
  SubscriptionUpgradeResponse,
} from "@/services/subscriptionService";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

interface SubscriptionState {
  currentPlan: SubscriptionPlan | null;
  availablePlans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  upgradeLoading: boolean;
  upgradeError: string | null;
}

const initialState: SubscriptionState = {
  currentPlan: null,
  availablePlans: [],
  loading: false,
  error: null,
  upgradeLoading: false,
  upgradeError: null,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    // Fetch current subscription
    fetchCurrentSubscriptionRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCurrentSubscriptionSuccess: (
      state,
      action: PayloadAction<SubscriptionPlan>,
    ) => {
      state.loading = false;
      state.currentPlan = action.payload;
      state.error = null;
    },
    fetchCurrentSubscriptionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch available plans
    fetchAvailablePlansRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAvailablePlansSuccess: (
      state,
      action: PayloadAction<SubscriptionPlan[]>,
    ) => {
      state.loading = false;
      state.availablePlans = action.payload;
      state.error = null;
    },
    fetchAvailablePlansFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Upgrade/Downgrade subscription
    upgradeSubscriptionRequest: (
      state,
      _action: PayloadAction<SubscriptionUpgradeRequest>,
    ) => {
      state.upgradeLoading = true;
      state.upgradeError = null;
    },
    upgradeSubscriptionSuccess: (
      state,
      _action: PayloadAction<SubscriptionUpgradeResponse>,
    ) => {
      state.upgradeLoading = false;
      state.upgradeError = null;
    },
    upgradeSubscriptionFailure: (state, action: PayloadAction<string>) => {
      state.upgradeLoading = false;
      state.upgradeError = action.payload;
    },

    // Reset state
    resetSubscriptionState: (state) => {
      state.upgradeLoading = false;
      state.upgradeError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchCurrentSubscriptionRequest,
  fetchCurrentSubscriptionSuccess,
  fetchCurrentSubscriptionFailure,
  fetchAvailablePlansRequest,
  fetchAvailablePlansSuccess,
  fetchAvailablePlansFailure,
  upgradeSubscriptionRequest,
  upgradeSubscriptionSuccess,
  upgradeSubscriptionFailure,
  resetSubscriptionState,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
